import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { AlertCircle, CheckCircle, ShieldAlert } from 'lucide-react';
import './AuthPages.css';

/**
 * /not-me/:token — B-65 (coordinator, 15 Oct). The "This wasn't me" link in
 * the Confirm your email message: someone signed up to Marakin with this
 * address. No sign-in needed (the link went to the mailbox). Nothing happens
 * until the button is pressed (mail scanners open links). The server then
 * removes the account's password, signs every device out and leaves the
 * email unconfirmed (POST /auth/not-me, lib/emailConfirm.js removeSquatter).
 */
const NotMePage = () => {
  const { token } = useParams();
  const [state, setState] = useState('ask'); // ask | done | error
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const go = async () => {
    setBusy(true); setError('');
    try {
      await axios.post('/auth/not-me', { token });
      setState('done');
    } catch (err) {
      setError(err?.response?.data?.error || 'Something went wrong. Please try the link again.');
      setState('error');
    } finally { setBusy(false); }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo-wrap">
          <img src="/marakin-logo-charcoal.png" alt="Marakin" className="auth-logo-img" />
        </div>
        {state === 'ask' && (
          <>
            <div className="confirm-gate-icon"><ShieldAlert size={26} /></div>
            <h1>This wasn't me</h1>
            <p className="auth-subtitle">
              Someone created a Marakin account with your email address. If it wasn't you, we'll sign them out
              everywhere and remove the password they set.
            </p>
            <button type="button" className="btn btn-primary auth-submit" onClick={go} disabled={busy}>
              {busy ? <span className="spinner" /> : 'Yes, sign them out'}
            </button>
            <p className="auth-switch">It was me after all? Just close this page.</p>
          </>
        )}
        {state === 'done' && (
          <>
            <div className="confirm-gate-icon confirm-gate-icon-ok"><CheckCircle size={26} /></div>
            <h1>Done</h1>
            <p className="auth-subtitle">
              Done. Anyone using a password on this account has been signed out. If you want to use Marakin, sign up
              or use Continue with Google/Apple.
            </p>
            <p className="auth-switch"><Link to="/login">Go to Marakin</Link></p>
          </>
        )}
        {state === 'error' && (
          <>
            <h1>This wasn't me</h1>
            <div className="auth-error" role="alert"><AlertCircle size={16} /> {error}</div>
            <p className="auth-switch"><Link to="/login">Go to Marakin</Link></p>
          </>
        )}
      </div>
    </div>
  );
};

export default NotMePage;
